import { supabase } from "@/lib/supabase-unified";
import { logger } from "@/lib/logger";

/** DB-side scope keys (Mixpanel uses the human-readable variants). */
export type SearchEventScope = "global" | "courses_hub" | "in_course";

export type SearchImpressionResult = {
  id: number | string;
  type: "course" | "lesson" | "collection";
};

/** Cap impression rows per search so a broad query can't fan out unboundedly. */
const MAX_IMPRESSIONS_PER_SEARCH = 20;

function toNumericId(id: number | string): number | null {
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

/**
 * Log one executed search (plus per-result impressions for CTR) to
 * `search_events` via the `log_search` RPC. Fire-and-forget: analytics must
 * never block or break the search UI.
 */
export function logSearchToSupabase(
  scope: SearchEventScope,
  query: string,
  results: SearchImpressionResult[],
  courseId?: number | null,
): void {
  const impressions = results
    .slice(0, MAX_IMPRESSIONS_PER_SEARCH)
    .map((r, index) => ({
      id: toNumericId(r.id),
      type: r.type,
      position: index + 1,
    }))
    .filter((r) => r.id != null);

  void supabase
    .rpc("log_search", {
      p_scope: scope,
      p_query: query,
      p_result_count: results.length,
      p_course_id: courseId ?? null,
      p_results: impressions,
    })
    .then(({ error }) => {
      if (error) logger.error("log_search failed", error);
    });
}

/** Log a search-result click to `search_events` via the `log_search_click` RPC. */
export function logSearchClickToSupabase(
  scope: SearchEventScope,
  query: string,
  result: SearchImpressionResult,
  position: number,
  courseId?: number | null,
): void {
  void supabase
    .rpc("log_search_click", {
      p_scope: scope,
      p_query: query,
      p_result_id: toNumericId(result.id),
      p_result_type: result.type,
      p_position: position,
      p_course_id: courseId ?? null,
    })
    .then(({ error }) => {
      if (error) logger.error("log_search_click failed", error);
    });
}
